import React from 'react';
import { Button, Input, Modal, Select, message } from 'antd';
import { DVICategorySection } from './DVICategorySection';
import { DVIPointRowNoteFields } from './DVIPointRow';
import {
  DVIInspectionFull, DVIInspectionItem, DVIItemStatus,
  DVICustomItemInput, DVIPointType,
} from '../../types/dvi';
import {
  getNotePresetGroups, DviNotePresetGroup,
} from '../../services/api/dviNotePresetsApi';

/**
 * Returns true when the item has been meaningfully addressed:
 * - status  → any non-pending status
 * - reading → has a non-blank reading_value
 * - photo   → at least one photo uploaded
 */
export function isItemSatisfied(it: DVIInspectionItem & { photos?: Array<unknown> }): boolean {
  const type = it.point_type ?? 'status';
  if (type === 'status') return it.status !== 'pending';
  if (type === 'reading') return !!it.reading_value && it.reading_value.trim() !== '';
  return (it.photos?.length ?? 0) > 0; // photo (and any other type)
}

export interface DVIInspectionFormProps {
  inspection: DVIInspectionFull;
  onItemStatusChange: (
    itemId: number,
    status: Exclude<DVIItemStatus, 'pending'>,
    notes?: string | null,
  ) => void;
  onSummaryChange: (notes: string) => void;
  onComplete: () => void;
  onConvert: (item: DVIInspectionItem) => void;
  onReadingChange?: (itemId: number, reading_value: string) => void;
  /** WP-07 — persist a note/measurement without requiring a status. */
  onNoteChange?: (itemId: number, fields: DVIPointRowNoteFields) => void;
  /** When true, privileged user can bypass required-but-not-mandatory items via confirm dialog. */
  canBypass?: boolean;
  /** When provided, renders an "Add custom item" control that appends an ad-hoc point. */
  onAddItem?: (input: DVICustomItemInput) => Promise<void>;
}

export const DVIInspectionForm: React.FC<DVIInspectionFormProps> = ({
  inspection, onItemStatusChange, onSummaryChange, onComplete, onConvert, onReadingChange,
  onNoteChange, canBypass = false, onAddItem,
}) => {
  const [summary, setSummary] = React.useState(inspection.summary_notes ?? '');

  /**
   * WP-07 — fetch every preset group ONCE for the whole inspection; each row
   * resolves its own group locally (see notePresets.ts). Per-row fetching would
   * be 30+ requests on a full inspection.
   *
   * Failure is deliberately non-fatal: presets are a convenience, and an
   * inspection must remain fillable with free-text notes if the call fails.
   */
  const [presetGroups, setPresetGroups] = React.useState<DviNotePresetGroup[]>([]);
  React.useEffect(() => {
    let cancelled = false;
    getNotePresetGroups()
      .then((groups) => { if (!cancelled) setPresetGroups(groups); })
      .catch(() => { if (!cancelled) setPresetGroups([]); });
    return () => { cancelled = true; };
  }, []);

  // "Add custom item" inline form state.
  const [addingItem, setAddingItem] = React.useState(false);
  const [newItemName, setNewItemName] = React.useState('');
  const [newItemType, setNewItemType] = React.useState<DVIPointType>('status');
  const [newItemCategory, setNewItemCategory] = React.useState('');
  const [addItemBusy, setAddItemBusy] = React.useState(false);

  const handleAddItemSubmit = async () => {
    if (!onAddItem || !newItemName.trim()) return;
    setAddItemBusy(true);
    try {
      await onAddItem({
        point_name: newItemName.trim(),
        point_type: newItemType,
        category: newItemCategory.trim() || undefined,
      });
      setNewItemName('');
      setNewItemType('status');
      setNewItemCategory('');
      setAddingItem(false);
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to add item');
    } finally {
      setAddItemBusy(false);
    }
  };

  // Badge: count only status-type items (display only — not used for gate).
  const statusItems = inspection.items.filter(i => (i.point_type ?? 'status') === 'status');
  const done = statusItems.filter(i => i.status !== 'pending').length;
  const total = statusItems.length;

  // Requirement-aware completion gate.
  const items = inspection.items;
  const mandatoryPending = items.filter(
    i => (i.requirement ?? 'optional') === 'mandatory' && !isItemSatisfied(i),
  );
  const requiredPending = items.filter(
    i => (i.requirement ?? 'optional') === 'required' && !isItemSatisfied(i),
  );
  const isDraft = inspection.status === 'draft';
  const blocked = mandatoryPending.length > 0 || (requiredPending.length > 0 && !canBypass);
  const needsConfirm = requiredPending.length > 0 && canBypass && mandatoryPending.length === 0;
  const canComplete = isDraft && items.length > 0 && !blocked;

  // Helper text rendered below the Complete button when blocked.
  let helperText: string | null = null;
  if (mandatoryPending.length > 0) {
    helperText = `Must complete: ${mandatoryPending.map(i => i.point_name).join(', ')}`;
  } else if (requiredPending.length > 0 && !canBypass) {
    helperText = `Required: ${requiredPending.map(i => i.point_name).join(', ')}`;
  }

  const handleCompleteClick = () => {
    if (needsConfirm) {
      Modal.confirm({
        title: 'Complete inspection?',
        content:
          'These required points are unfinished: ' +
          requiredPending.map(i => i.point_name).join(', ') +
          '. Complete anyway?',
        onOk: onComplete,
      });
    } else {
      onComplete();
    }
  };

  // Group items by category, preserving sort_order.
  const byCategory = new Map<string, DVIInspectionItem[]>();
  for (const item of inspection.items) {
    const list = byCategory.get(item.category_name) ?? [];
    list.push(item);
    byCategory.set(item.category_name, list);
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ padding: '8px 12px', fontSize: 14, color: '#666' }}>
        <strong>{done}/{total} points</strong> completed
      </div>
      {Array.from(byCategory.entries()).map(([categoryName, catItems]) => (
        <DVICategorySection
          key={categoryName}
          categoryName={categoryName}
          items={catItems}
          onItemStatusChange={onItemStatusChange}
          onConvert={onConvert}
          onReadingChange={onReadingChange}
          onNoteChange={onNoteChange}
          presetGroups={presetGroups}
        />
      ))}
      {onAddItem && (
        <div style={{ padding: '12px 12px 0' }}>
          {!addingItem ? (
            <Button
              block
              size="large"
              style={{ minHeight: 44 }}
              onClick={() => setAddingItem(true)}
            >
              + Add custom item
            </Button>
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 8,
              border: '1px dashed #bbb', borderRadius: 6, padding: 12,
            }}>
              <label style={{ fontWeight: 600 }}>Add custom inspection item</label>
              <Input
                size="large"
                placeholder="Item name (e.g. Rear differential fluid)"
                value={newItemName}
                onChange={e => setNewItemName(e.target.value)}
                maxLength={200}
                autoFocus
              />
              <Select<DVIPointType>
                size="large"
                value={newItemType}
                onChange={setNewItemType}
                options={[
                  { value: 'status', label: 'Status check (Pass / Attention / Fail)' },
                  { value: 'photo', label: 'Photo' },
                  { value: 'reading', label: 'Reading / measurement' },
                ]}
              />
              <Input
                size="large"
                placeholder='Category (optional — defaults to "Additional Items")'
                value={newItemCategory}
                onChange={e => setNewItemCategory(e.target.value)}
                maxLength={100}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="large"
                  style={{ flex: 1, minHeight: 44 }}
                  onClick={() => setAddingItem(false)}
                  disabled={addItemBusy}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  style={{ flex: 1, minHeight: 44 }}
                  disabled={!newItemName.trim()}
                  loading={addItemBusy}
                  onClick={handleAddItemSubmit}
                >
                  Add item
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <div style={{ padding: '12px' }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
          Summary notes (for customer)
        </label>
        <Input.TextArea
          rows={3}
          value={summary}
          onChange={e => setSummary(e.target.value)}
          onBlur={() => onSummaryChange(summary)}
          placeholder="Overall takeaway, recommended next steps, etc."
        />
      </div>
      <div style={{
        position: 'sticky', bottom: 0,
        background: 'white', padding: 12,
        borderTop: '1px solid #ddd',
      }}>
        <button
          disabled={!canComplete}
          onClick={handleCompleteClick}
          aria-label="Complete inspection"
          style={{
            display: 'block', width: '100%', padding: '12px 0',
            fontSize: 16, fontWeight: 700, cursor: canComplete ? 'pointer' : 'not-allowed',
            background: canComplete ? '#1890ff' : '#ccc',
            color: 'white', border: 'none', borderRadius: 6,
          }}
        >
          {inspection.status === 'completed' ? '✓ Completed' : 'Complete Inspection'}
        </button>
        {helperText && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#d9534f' }}>
            {helperText}
          </div>
        )}
      </div>
    </div>
  );
};

export default DVIInspectionForm;
